import { afterEveryRender, Component, contentChild, effect, ElementRef, inject, input, signal } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MibIconComponent } from '../icons';

/**
 * A search input with a magnifier at its start and a button that clears it at its end.
 *
 * ```html
 * <mib-search-field clearLabel="Clear the search">
 *   <input type="search" class="mib-input" aria-label="Search cases" [formControl]="query" />
 * </mib-search-field>
 * ```
 */
@Component({
  selector: 'mib-search-field',
  imports: [MibIconComponent],
  templateUrl: './search-field.component.html',
  host: {
    class:
      'relative block [&>input]:w-full [&>input]:pr-11 [&>input]:pl-9 ' +
      '[&>input::-webkit-search-cancel-button]:appearance-none',
    '(input)': 'refresh()',
  },
})
export class MibSearchFieldComponent {
  /** Accessible name of the button that clears the field, given translated. */
  readonly clearLabel = input.required<string>();

  protected readonly clearable = signal(false);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ngControl = contentChild(NgControl, { descendants: true });

  constructor() {
    /* Writes that emit nothing, such as `setValue(v, { emitEvent: false })` or `[value]`, show only once rendered. */
    afterEveryRender({ read: () => this.refresh() });
    /* Zoneless, typing in a plain input or a control write outside a render schedules no render: these two do. */
    effect((onCleanup) => {
      const events = this.ngControl()?.control?.events;
      if (!events) return;
      const subscription = events.subscribe(() => this.refresh());
      onCleanup(() => subscription.unsubscribe());
    });
  }

  /* The bubbling `input` event lets any value accessor, reactive or template driven, see the empty value. */
  protected clear(): void {
    const field = this.field();
    if (!field) return;
    field.value = '';
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.focus();
  }

  protected refresh(): void {
    const field = this.field();
    if (!field) return;
    const control = this.ngControl();
    /* A control disables its input only after it emits, so its own state is read rather than the input's. */
    const disabled = control ? !!control.disabled : field.disabled;
    this.clearable.set(field.value !== '' && !disabled && !field.readOnly);
  }

  private field(): HTMLInputElement | null {
    return this.host.nativeElement.querySelector(':scope > input');
  }
}
